import React, { useEffect, useMemo, useState } from "react"
import { useParams, useLocation } from "react-router-dom"
import {
    Box,
    Container,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    FormGroup,
    RadioGroup,
    Radio,
    CircularProgress,
    Paper,
    ImageList,
    ImageListItem,
    Button,
    Stack,
    Link,
} from "@mui/material"
import { toast } from "react-toastify"
import api from "../../services/api"

import InputMask from "react-input-mask"

import { WhatsApp, Person, Email } from "@mui/icons-material"

import config from "../../config/config.json"

import combineQuestionsAndAnswers from "./combineQuestions"
import ModalMediaView from "./ModalMediaView"

function FormView() {
    const { uuid } = useParams()
    const location = useLocation()

    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search],
    )
    const utm_source = queryParams.get("utm_source") || ""
    const isPreview = location.pathname.startsWith("/form-preview")

    const [formSent, setFormSent] = useState(false)
    const [loading, setLoading] = useState(false)
    const [form, setForm] = useState(null)
    const [answers, setAnswers] = useState({})

    const [name, setName] = useState("")
    const [email, setEmail] = useState("")
    const [number, setNumber] = useState("")

    const [open, setOpen] = useState(false)
    const [currentIndex, setCurrentIndex] = useState(0)

    const handleOpen = (index) => {
        setCurrentIndex(index)
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % form.product.medias.length)
    }

    const handlePrev = () => {
        setCurrentIndex(
            (prev) =>
                (prev - 1 + form.product.medias.length) %
                form.product.medias.length,
        )
    }

    async function getForm() {
        try {
            setLoading(true)
            let response

            if (isPreview) {
                response = await api.get(`/forms/${uuid}/preview`)
            } else {
                response = await api.get(`/forms/u/${uuid}`)
            }

            setForm(response.data)
        } catch (error) {
            toast.error("Erro ao carregar formulário.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getForm()
    }, [])

    if (loading || !form) {
        return (
            <Box
                height="100vh"
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <CircularProgress />
            </Box>
        )
    }

    const handleAnswerChange = (questionId, value) => {
        setAnswers((prev) => ({
            ...prev,
            [questionId]: value,
        }))
    }

    function handleStartWhatsApp() {
        const responses = combineQuestionsAndAnswers(form.questions, answers)

        if (!form.agentNumber) {
            toast.error("Número de WhatsApp não configurado.")
            return
        }

        // Monta a mensagem
        let message = `Olá! Acabei de preencher o formulário "${form.title}". Aqui estão minhas respostas:\n\n`

        responses.forEach((resp) => {
            message += `• ${resp.questionText}: ${resp.answer}\n`
        })

        const encodedMessage = encodeURIComponent(message)

        const whatsappUrl = `https://wa.me/${form.agentNumber}?text=${encodedMessage}`

        window.open(whatsappUrl, "_blank")
    }

    const handleSubmit = async () => {
        try {
            setLoading(true)
            if (!name) {
                toast.error("Nome é obrigatório")
                return
            }
            if (!email) {
                toast.error("Email é obrigatório")
                return
            }
            if (!number) {
                toast.error("WhatsApp é obrigatório")
                return
            }

            const payload = {
                name,
                number: number.replace(/\D/g, ""),
                formId: form.id,
                email,
                plaftorm: utm_source,
                answers: Object.entries(answers).map(([questionId, value]) => ({
                    questionId: Number(questionId),
                    answer:
                        typeof value === "string"
                            ? value
                            : JSON.stringify(value),
                })),
            }

            await api.post(`/form-responses`, payload)

            toast.success("Respostas enviadas com sucesso!")
            setLoading(false)
            setFormSent(true)
        } catch (error) {
            toast.error("Erro ao enviar respostas.")
            setLoading(false)
        }
    }

    return (
        <Box
            sx={{
                backgroundColor: form.primaryColor,
            }}
        >
            <ModalMediaView
                open={open}
                currentIndex={currentIndex}
                form={form}
                handleClose={handleClose}
                handleNext={handleNext}
                handlePrev={handlePrev}
            />
            <Box
                sx={{
                    height: "100vh",
                    overflowY: "scroll",
                    py: 4,
                }}
            >
                <Container maxWidth="sm">
                    <Paper elevation={4} sx={{ p: 4, borderRadius: 2 }}>
                        {form.logoUrl && (
                            <Box display="flex" justifyContent="center" mb={2}>
                                <img
                                    src={form.logoUrl}
                                    alt="Logo da empresa"
                                    style={{
                                        maxHeight: 64,
                                        objectFit: "contain",
                                    }}
                                />
                            </Box>
                        )}

                        {formSent && (
                            <>
                                <Typography
                                    variant="h6"
                                    color="primary"
                                    textAlign="center"
                                    marginTop={2}
                                >
                                    Sua resposta foi enviada com sucesso
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="primary"
                                    textAlign="center"
                                    marginBottom={3}
                                >
                                    Você pode iniciar o atendimento agora mesmo
                                    para tirar mais dúvidas e responderemos
                                    assim que possível.
                                </Typography>

                                <Button
                                    startIcon={<WhatsApp />}
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                    onClick={handleStartWhatsApp}
                                >
                                    Iniciar conversa
                                </Button>
                            </>
                        )}

                        {!formSent && (
                            <>
                                <Typography
                                    variant="h4"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    {form.title}
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    {form.description}
                                </Typography>

                                {form.product && (
                                    <>
                                        <Box mt={3} mb={2}>
                                            <Typography
                                                variant="h6"
                                                gutterBottom
                                            >
                                                {form.product.name}
                                            </Typography>
                                            <Typography variant="body2">
                                                {form.product.description}
                                            </Typography>
                                            {form.backgroundUrl && (
                                                <Box mt={2}>
                                                    <img
                                                        src={form.backgroundUrl}
                                                        alt={form.product.name}
                                                        style={{
                                                            width: "100%",
                                                            borderRadius: 8,
                                                        }}
                                                    />
                                                </Box>
                                            )}

                                            {form.product.medias?.length >
                                                0 && (
                                                <Box
                                                    sx={{
                                                        width: 500,
                                                        height: 250,
                                                        overflowY: "scroll",
                                                    }}
                                                >
                                                    <ImageList
                                                        cols={2}
                                                        gap={8}
                                                        sx={{ mt: 2 }}
                                                    >
                                                        {form.product.medias.map(
                                                            (media, index) => (
                                                                <ImageListItem
                                                                    key={
                                                                        media.id
                                                                    }
                                                                    onClick={() =>
                                                                        handleOpen(
                                                                            index,
                                                                        )
                                                                    }
                                                                    style={{
                                                                        cursor: "pointer",
                                                                    }}
                                                                >
                                                                    {media.mediaType ===
                                                                    "image" ? (
                                                                        <img
                                                                            src={
                                                                                media.fileFullURL
                                                                            }
                                                                            alt={
                                                                                media.title ||
                                                                                "Imagem do produto"
                                                                            }
                                                                            loading="lazy"
                                                                            style={{
                                                                                borderRadius: 8,
                                                                            }}
                                                                        />
                                                                    ) : media.mediaType ===
                                                                      "video" ? (
                                                                        <video
                                                                            controls
                                                                            width="100%"
                                                                            style={{
                                                                                borderRadius: 8,
                                                                            }}
                                                                        >
                                                                            <source
                                                                                src={
                                                                                    media.fileFullURL
                                                                                }
                                                                                type={`video/${media.extension}`}
                                                                            />
                                                                            Seu
                                                                            navegador
                                                                            não
                                                                            suporta
                                                                            vídeos.
                                                                        </video>
                                                                    ) : null}
                                                                </ImageListItem>
                                                            ),
                                                        )}
                                                    </ImageList>
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}

                                <Box mt={4}>
                                    <Stack
                                        direction="column"
                                        spacing={2}
                                        marginBottom={2}
                                    >
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder={"Digite seu nome"}
                                            label="Nome"
                                            slotProps={{
                                                input: {
                                                    endAdornment: <Person />,
                                                },
                                            }}
                                            value={name}
                                            onChange={(e) =>
                                                setName(e.target.value)
                                            }
                                        />
                                        <InputMask
                                            prefix="teste"
                                            mask={"55 (99)99999-9999"}
                                            value={number}
                                            onChange={(e) =>
                                                setNumber(e.target.value)
                                            }
                                        >
                                            {(inputProps) => (
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    placeholder={
                                                        "Digite seu WhatsApp"
                                                    }
                                                    label="WhatsApp"
                                                    {...inputProps}
                                                    slotProps={{
                                                        input: {
                                                            endAdornment: (
                                                                <WhatsApp />
                                                            ),
                                                        },
                                                    }}
                                                />
                                            )}
                                        </InputMask>

                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder={"Digite seu E-mail"}
                                            label="E-mail"
                                            slotProps={{
                                                input: {
                                                    endAdornment: <Email />,
                                                },
                                            }}
                                            value={email}
                                            onChange={(e) =>
                                                setEmail(e.target.value)
                                            }
                                        />
                                    </Stack>

                                    {form.questions.map((question) => (
                                        <Box key={question.id} mb={3}>
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={500}
                                                gutterBottom
                                            >
                                                {question.questionText}
                                            </Typography>

                                            {question.type === "text" && (
                                                <TextField
                                                    fullWidth
                                                    variant="outlined"
                                                    value={
                                                        answers[question.id] ||
                                                        ""
                                                    }
                                                    onChange={(e) =>
                                                        handleAnswerChange(
                                                            question.id,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}

                                            {question.type ===
                                                "single_select" && (
                                                <RadioGroup
                                                    value={
                                                        answers[question.id] ||
                                                        ""
                                                    }
                                                    onChange={(e) =>
                                                        handleAnswerChange(
                                                            question.id,
                                                            e.target.value,
                                                        )
                                                    }
                                                >
                                                    {JSON.parse(
                                                        question.questionOptions ||
                                                            "[]",
                                                    ).map((opt) => (
                                                        <FormControlLabel
                                                            key={opt.id}
                                                            value={opt.id}
                                                            control={<Radio />}
                                                            label={opt.label}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            )}

                                            {question.type ===
                                                "multi_select" && (
                                                <FormGroup>
                                                    {JSON.parse(
                                                        question.questionOptions ||
                                                            "[]",
                                                    ).map((opt) => (
                                                        <FormControlLabel
                                                            key={opt.id}
                                                            control={
                                                                <Checkbox
                                                                    checked={(
                                                                        answers[
                                                                            question
                                                                                .id
                                                                        ] || []
                                                                    ).includes(
                                                                        opt.id,
                                                                    )}
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        const selected =
                                                                            answers[
                                                                                question
                                                                                    .id
                                                                            ] ||
                                                                            []
                                                                        handleAnswerChange(
                                                                            question.id,
                                                                            e
                                                                                .target
                                                                                .checked
                                                                                ? [
                                                                                      ...selected,
                                                                                      opt.id,
                                                                                  ]
                                                                                : selected.filter(
                                                                                      (
                                                                                          id,
                                                                                      ) =>
                                                                                          id !==
                                                                                          opt.id,
                                                                                  ),
                                                                        )
                                                                    }}
                                                                />
                                                            }
                                                            label={opt.label}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            )}
                                        </Box>
                                    ))}
                                </Box>

                                {!isPreview && (
                                    <Box>
                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                        >
                                            <Button color="secondary">
                                                Limpar
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={handleSubmit}
                                            >
                                                Enviar
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </Paper>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        marginTop={2}
                    >
                        {config.system.name === "SyncCRM" ? (
                            <Typography variant="caption">
                                SyncCRM by SoftFY
                            </Typography>
                        ) : (
                            <Typography variant="caption">
                                {config.system.name}
                            </Typography>
                        )}
                        {config.system.name === "SyncCRM" && (
                            <Link
                                href="https://painel.synccrm.com.br/signup"
                                target="_blank"
                            >
                                Teste grátis por 7 dias
                            </Link>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    )
}

export default FormView
